/**
 * Entwurf-Persistenz für den Antwort-Editor (Support-Postfach-Neugestaltung, 23.09.2026).
 *
 * Grund für Ben: der alte, fixierte Editor verlor jeden angefangenen Text beim versehentlichen
 * Wegklicken. `localStorage`-Schlüssel `bis_support_entwurf:<ticketId>` — pro Ticket EIN Entwurf
 * (Modus + Text), entprellt geschrieben, nach erfolgreichem Senden gelöscht. Browser-Speicher ist
 * eine Komfort-Funktion (Kern-Regel-Konvention im Projekt) — jeder Zugriff steht in try/catch.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type EntwurfModus = 'antwort' | 'notiz';

interface EntwurfDaten {
  modus: EntwurfModus;
  text: string;
}

/** Ein Entwurf ohne Schreibzugriff seit 30 Tagen gilt als hinfällig (vermutlich ein längst
 * geschlossenes oder woanders beantwortetes Ticket) und wird beim Laden entfernt. */
const ENTWURF_VERFALL_MS = 30 * 24 * 60 * 60 * 1000;
const ENTWURF_PRAEFIX = 'bis_support_entwurf:';

function schluessel(ticketId: string): string {
  return `${ENTWURF_PRAEFIX}${ticketId}`;
}

function ladeVonSpeicher(ticketId: string | null): EntwurfDaten | null {
  if (!ticketId) return null;
  try {
    const roh = localStorage.getItem(schluessel(ticketId));
    if (!roh) return null;
    const daten = JSON.parse(roh) as Partial<EntwurfDaten> & { gespeichertAm?: number };
    if (typeof daten.text !== 'string' || !daten.text.trim()) return null;
    if (typeof daten.gespeichertAm === 'number' && Date.now() - daten.gespeichertAm > ENTWURF_VERFALL_MS) {
      localStorage.removeItem(schluessel(ticketId));
      return null;
    }
    return { modus: daten.modus === 'notiz' ? 'notiz' : 'antwort', text: daten.text };
  } catch {
    return null;
  }
}

/** Einzige Schreibstelle — sowohl für den entprellten Timer als auch für das Sofort-Schreiben
 * beim Ticketwechsel/Verlassen. Leerer Text löscht den Eintrag statt eines leeren Entwurfs. */
function schreibeEntwurf(ticketId: string, modus: EntwurfModus, text: string): void {
  try {
    if (text.trim()) {
      localStorage.setItem(schluessel(ticketId), JSON.stringify({ modus, text, gespeichertAm: Date.now() }));
    } else {
      localStorage.removeItem(schluessel(ticketId));
    }
  } catch {
    // Browser-Speicher ist eine Komfort-Funktion — ein Fehler hier darf das Schreiben nicht stören.
  }
}

/** Für die Ticket-Liste: zeigt ein Entwurf-Symbol, ohne den Entwurf selbst zu laden. */
export function hatEntwurf(ticketId: string): boolean {
  return ladeVonSpeicher(ticketId) !== null;
}

/** Beim Abmelden ALLE Support-Entwürfe dieses Browsers entfernen — sonst sieht der nächste
 * Login auf demselben Gerät (anderer Superadmin) fremde, unversendete Antwortentwürfe. */
export function loescheAlleSupportEntwuerfe(): void {
  try {
    const zuLoeschen: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(ENTWURF_PRAEFIX)) zuLoeschen.push(key);
    }
    zuLoeschen.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Browser-Speicher ist eine Komfort-Funktion.
  }
}

export function useEntwurf(ticketId: string | null) {
  // Ticketwechsel WÄHREND des Renderns nachziehen (React-Muster "State an eine geänderte Prop
  // anpassen" statt Effekt+setState — vermeidet einen unnötigen Zwischen-Render und das
  // `react-hooks/set-state-in-effect`-Warnsignal für synchrones setState in einem Effekt).
  const [geladenFuerTicketId, setGeladenFuerTicketId] = useState(ticketId);
  const [modus, setModus] = useState<EntwurfModus>(() => ladeVonSpeicher(ticketId)?.modus ?? 'antwort');
  const [text, setText] = useState(() => ladeVonSpeicher(ticketId)?.text ?? '');
  const [gespeichertUm, setGespeichertUm] = useState<Date | null>(null);
  // Letzter bekannter Stand (modus + text) — NUR in einem Effekt fortgeschrieben, nie während des
  // Renderns gelesen/geschrieben (React-Ref-Regel, `react-hooks/refs`). Dient dem
  // Ticketwechsel-/Unmount-/pagehide-Flush weiter unten: die Ref hält immer den ZULETZT im Editor
  // gesehenen Stand, unabhängig davon, ob die 600ms-Entprellung schon geschrieben hat.
  const standRef = useRef<{ modus: EntwurfModus; text: string }>({ modus, text });

  if (ticketId !== geladenFuerTicketId) {
    setGeladenFuerTicketId(ticketId);
    const geladen = ladeVonSpeicher(ticketId);
    setModus(geladen?.modus ?? 'antwort');
    setText(geladen?.text ?? '');
    setGespeichertUm(null);
  }

  // Immer der AKTUELLE ticketId-Wert dieses Hooks — NUR im Effekt unten fortgeschrieben, nie
  // während des Renderns (React-Ref-Regel, wie `standRef`) — damit `loeschen()`
  // (Finding 8) prüfen kann, ob zwischen "Senden ausgelöst" und "Senden abgeschlossen" (await)
  // schon zu einem ANDEREN Ticket gewechselt wurde.
  const aktuelleTicketIdRef = useRef(ticketId);

  // Finding 6 (Runde 4): ZWEI GETRENNTE Effekte statt einem — vorher lag die 600ms-Entprellung
  // UND der Sofort-Flush im selben Effekt mit `[ticketId, modus, text]` als Deps. React räumt den
  // Cleanup eines Effekts bei JEDER Dep-Änderung ab, also bei JEDEM Tastendruck (weil `text`
  // selbst eine Dep war) — und der Cleanup schrieb dabei sofort in den `localStorage`. Die
  // Entprellung wirkte dadurch nie: jeder Tastendruck loeste seinen eigenen Sofort-Flush aus,
  // BEVOR der 600ms-Timer je ablaufen konnte.
  //
  // Effekt A: reine Entprellung. Läuft bei jeder Änderung von ticketId/modus/text, der Cleanup
  // räumt den Timer NUR ab (kein Flush) — nur wenn 600ms lang KEINE weitere Änderung folgt, feuert
  // der Timer und schreibt tatsächlich.
  useEffect(() => {
    standRef.current = { modus, text };
    if (!ticketId) return;
    // Unverändert gegenüber dem geladenen Stand: nichts tun (sonst zeigt „Entwurf gespeichert“
    // fälschlich sofort nach dem blossen Öffnen eines Tickets mit vorhandenem Entwurf an).
    const bestehend = ladeVonSpeicher(ticketId);
    if (bestehend?.modus === modus && bestehend?.text === text) return;

    const timer = setTimeout(() => {
      schreibeEntwurf(ticketId, modus, text);
      if (text.trim()) setGespeichertUm(new Date());
    }, 600);

    return () => clearTimeout(timer);
  }, [ticketId, modus, text]);

  // Effekt B: Sofort-Flush NUR bei Ticketwechsel, Unmount oder Seite-verlassen (`pagehide`) — hängt
  // ausschliesslich an `ticketId`, NICHT an modus/text (die kommen aus `standRef`, immer aktuell
  // gehalten von Effekt A). Läuft dadurch NICHT bei jedem Tastendruck, sondern nur, wenn sich
  // `ticketId` tatsächlich ändert oder die Komponente verschwindet.
  useEffect(() => {
    aktuelleTicketIdRef.current = ticketId;

    const flush = () => { if (ticketId) schreibeEntwurf(ticketId, standRef.current.modus, standRef.current.text); };

    // Seite verlassen (Tab schliessen/neu laden) — `pagehide` statt `beforeunload`, das den
    // Zurück/Vorwärts-Cache (bfcache) verhindern würde (Finding 8).
    window.addEventListener('pagehide', flush);

    return () => {
      window.removeEventListener('pagehide', flush);
      // Ticketwechsel/Unmount, ggf. VOR Ablauf der 600ms-Entprellung — den ausstehenden Stand DER
      // ALTEN ticketId (aus der Closure) sofort schreiben, sonst geht der letzte Tastendruck
      // lautlos verloren.
      flush();
    };
  }, [ticketId]);

  const loeschen = useCallback(() => {
    if (!ticketId) return;
    try {
      localStorage.removeItem(schluessel(ticketId));
    } catch {
      // s.o.
    }
    // Finding 8: das Textfeld nur leeren, wenn NACH dem Senden noch dasselbe Ticket offen ist —
    // sonst würde ein spät abschliessendes Senden (Ticket A) das inzwischen für Ticket B
    // geladene Textfeld leeren.
    if (aktuelleTicketIdRef.current === ticketId) {
      setText('');
      setGespeichertUm(null);
    }
  }, [ticketId]);

  return { modus, setModus, text, setText, gespeichertUm, loeschen };
}
